import React, { useEffect, useRef } from 'react';
import useAppContext from 'flow/hooks/useAppContext';
import useReportScreenShareAspect from 'flow/hooks/useReportScreenShareAspect';
import NamePlate from '../overlays/NamePlate';
import TileVideoStatsPlate from '../overlays/TileVideoStatsPlate';

interface TileStageScreenShareProps {
    /** The shared-screen MediaStream, or null until it lands. */
    stream: MediaStream | null;
    /**
     * Presenter's display name for the "<Name>'s screen" plate. Omitted for the
     * local user's own share — the "You are presenting" badge already names it,
     * so a second self-label would be redundant.
     */
    presenterName?: string;
    /**
     * The stream/peer driving the video. Keys the imperative attach + aspect
     * reporting so a late-arriving or reconnecting stream is re-attached. For a
     * local share this is the stream itself; for a remote share it's the peer.
     */
    sourceKey?: unknown;
    /**
     * Signaling name of the presenter, for the debug stats read-out (VID-396).
     * During a share the presenter's PRIMARY receive leg carries the screen, so
     * the stats registered under their name are this tile's numbers — without
     * this the heaviest feed in the meeting had no read-out anywhere, since the
     * presenter's grid tile is hidden while their screen owns the stage. Omitted
     * for the local user's own share, which is a send leg with no receiver stats.
     */
    statsMemberName?: string | null;
}

/**
 * The single on-stage shared-screen tile — used identically whether the screen
 * comes from the local user or a remote presenter (see TileLocalStageScreenShare
 * / TileRemoteStageScreenShare, which resolve the stream). This is the *screen*
 * role: shown whole (contained, never mirrored or cover-cropped), no pin, no
 * mic/camera status, and no active-speaker frame (VID-214 AC10). Camera concerns
 * live in MeetingLocalVideo / TileVideoParticipant instead.
 */
const TileStageScreenShare: React.FC<TileStageScreenShareProps> = ({
    stream,
    presenterName,
    sourceKey,
    statsMemberName,
}) => {
    const { i18next } = useAppContext();
    const videoRef = useRef<HTMLVideoElement>(null);

    // Report the shared screen's true aspect so the layout engine sizes the
    // stage to it instead of assuming landscape. The stage tile only mounts
    // while a screen is on it, so it's always the active reporter.
    useReportScreenShareAspect(videoRef, true, sourceKey ?? stream);

    // Attach the stream imperatively. The <video> is always mounted, so unlike
    // the camera tile there's no render-order dance — we just (re)assign on
    // stream/source change. iOS Safari won't autoplay an inline <video> without
    // playsinline (it parks it on a tap-to-play poster), so opt in and start it.
    useEffect(() => {
        const video = videoRef.current;
        if (!video) {
            return undefined;
        }
        video.srcObject = stream;
        if (stream) {
            video.muted = true;
            video.playsInline = true;
            video.setAttribute('playsinline', ''); // older WebKit
            video.play().catch((err) => {
                console.warn(
                    '[TileStageScreenShare] screen-share play() failed:',
                    err,
                );
            });
        }
        return () => {
            video.srcObject = null;
        };
    }, [stream, sourceKey]);

    return (
        <div className="relative flex h-full w-full overflow-hidden rounded-xl bg-black text-white">
            <video
                ref={videoRef}
                autoPlay={true}
                playsInline={true}
                muted={true}
                // A shared screen is shown whole (contained within the
                // true-aspect stage), never cover-cropped or mirrored.
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
            {statsMemberName ? (
                // Same top-start corner as the grid tiles (RemoteVideoLabels),
                // so the read-out is where a debug session expects it.
                <div className="absolute start-3 top-3 flex gap-2">
                    <TileVideoStatsPlate memberName={statsMemberName} />
                </div>
            ) : null}
            {presenterName ? (
                <div className="absolute inset-x-3 bottom-3 flex gap-2">
                    <NamePlate
                        label={i18next.t('videoconference.screenshare_screen', {
                            Name: presenterName,
                        })}
                    />
                </div>
            ) : null}
        </div>
    );
};

export default TileStageScreenShare;
